/**
 * useTimezone composable — tms-app-soho
 *
 * Wraps the pure timezone-aware date utilities from the canonical package
 * and pre-binds them to the user's current IANA timezone.
 *
 * This shim exists so that unplugin-auto-import can discover `useTimezone`
 * and `nowUtcIso` via the `dirs` config in vite.config.mts.
 * The canonical implementation lives in packages/tms-vue/src/utils/datetime.util.ts.
 *
 * Imports via the sub-path `@tms-phoenix/ui-vue/utils/datetime.util` so that
 * `jest.mock('@tms-phoenix/ui-vue', ...)` in test files does NOT shadow these
 * pure utility functions.
 */
import {
  formatDateTime,
  formatDate,
  utcIsoToPickerDate,
  pickerDateToUtcIso,
  utcIsoToDatetimeLocal,
  nowPlusHoursInUserTz,
  nowUtcIso,
  todayInUserTz,
  isBeforeToday,
  parseDateOnlyToPickerDate,
  pickerDateToDateOnly,
} from '@tms-phoenix/ui-vue/utils/datetime.util';

export { nowUtcIso };

export function useTimezone() {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return {
    timeZone,
    formatDateTime: (utcIso: string | null | undefined) => formatDateTime(utcIso, timeZone),
    formatDate: (v: string | null | undefined) => formatDate(v, timeZone),
    utcIsoToPickerDate: (utcIso: string | null | undefined) => utcIsoToPickerDate(utcIso, timeZone),
    pickerDateToUtcIso: (date: Date | null | undefined) => pickerDateToUtcIso(date, timeZone),
    utcIsoToDatetimeLocal: (utcIso: string | null | undefined) => utcIsoToDatetimeLocal(utcIso, timeZone),
    nowPlusHoursInUserTz: (hours: number) => nowPlusHoursInUserTz(hours, timeZone),
    todayInUserTz: () => todayInUserTz(timeZone),
    isBeforeToday: (dateStr: string) => isBeforeToday(dateStr, timeZone),
    parseDateOnlyToPickerDate,
    pickerDateToDateOnly,
  };
}

